export const ALL_TEAM_MEDIA_ROUNDS = "__all_team_media_rounds__";

export type TeamMediaTarget = Readonly<{ roundId: string; title: string; roundVersion: number; entryId: string; entryVersion: number }>;
export type TeamMediaAttempt = Readonly<{ target: TeamMediaTarget; body: Readonly<{ requestId: string; expectedVersion: number; expectedEntryVersion: number; mediaId: string }> }>;
export type TeamMediaBatch = { readonly attempts: readonly TeamMediaAttempt[]; readonly saved: Set<string>; running: boolean };

/** Capture all versions and retry identities before the first mutation. */
export function createTeamMediaBatch(targets: readonly TeamMediaTarget[], mediaId: string, uuid: () => string): TeamMediaBatch {
  const ids = new Set<string>(), requests = new Set<string>();
  if (!mediaId.trim() || !targets.length || targets.length > 1000) throw new Error("Invalid media batch");
  const attempts = targets.map(target => {
    if (!target.roundId.trim() || target.roundId === ALL_TEAM_MEDIA_ROUNDS || !target.entryId.trim() || ids.has(target.roundId) ||
      !Number.isSafeInteger(target.roundVersion) || target.roundVersion < 0 || target.roundVersion >= Number.MAX_SAFE_INTEGER ||
      !Number.isSafeInteger(target.entryVersion) || target.entryVersion < 0) throw new Error("Invalid media target");
    ids.add(target.roundId);
    const requestId = uuid();
    if (!requestId.trim() || requests.has(requestId)) throw new Error("Invalid media request identity");
    requests.add(requestId);
    return Object.freeze({ target: Object.freeze({ ...target }), body: Object.freeze({ requestId, expectedVersion: target.roundVersion, expectedEntryVersion: target.entryVersion, mediaId }) });
  });
  return { attempts: Object.freeze(attempts), saved: new Set(), running: false };
}

/** Each round has its own receipt. An interrupted batch never resends confirmed rounds. */
export async function saveTeamMediaBatch(batch: TeamMediaBatch, send: (attempt: TeamMediaAttempt) => Promise<unknown>, progress: () => void): Promise<void> {
  if (batch.running) return;
  batch.running = true;
  try {
    for (const attempt of batch.attempts) {
      if (batch.saved.has(attempt.target.roundId)) continue;
      const result = await send(attempt) as Record<string, unknown> | null;
      if (!result || result.roundId !== attempt.target.roundId || result.entryId !== attempt.target.entryId || result.mediaId !== attempt.body.mediaId || result.version !== attempt.body.expectedVersion + 1) throw new Error("Unconfirmed media receipt");
      batch.saved.add(attempt.target.roundId); progress();
    }
  } finally { batch.running = false; }
}
